import React, { useContext, useState } from "react";
import {
  Box, Typography, Alert, CircularProgress, TextField,
  Button, InputAdornment, IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import UserContext from "./UserContext";
import axios from "axios";
import ThemeToggle from "./ThemeToggle";
import { extractErrorDetail } from "./validators";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

const BRAND = {
  navy: "#00357a",
  navyDark: "#001f4d",
  gold: "#c9a96e",
  offWhite: "#f8f6f2",
};

function Field({ label, required, hint, isDark, children }) {
  const sub = isDark ? "rgba(255,255,255,0.55)" : "#475569";
  return (
    <Box sx={{ mb: 2.5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography sx={{ fontSize: 14.5, fontWeight: 700, letterSpacing: "0.06em", color: sub, textTransform: "uppercase" }}>
          {label}
          {required && <Box component="span" sx={{ color: BRAND.gold, ml: 0.4 }}>*</Box>}
        </Typography>
        {hint && <Typography sx={{ fontSize: 14, color: sub }}>{hint}</Typography>}
      </Box>
      {children}
    </Box>
  );
}

const FEATURES = [
  { icon: "🔬", text: "AI-powered lab report analysis" },
  { icon: "🔒", text: "HIPAA-compliant & private" },
  { icon: "🌐", text: "16-language support" },
  { icon: "📈", text: "Longitudinal trend memory" },
];

function LoginPage() {
  const { setUser } = useContext(UserContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [email, setEmail]     = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw]   = useState(false);

  const paymentJustCompleted = searchParams.get("payment") === "success";

  const bg     = isDark ? "#080d1a" : "#f4f7ff";
  const cardBg = isDark ? "#111827" : "#ffffff";
  const textSub = isDark ? "#94a3b8" : "#64748b";

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      bgcolor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,35,96,0.018)",
      fontSize: 16,
      "& fieldset": { borderColor: isDark ? "rgba(255,255,255,0.09)" : "rgba(0,53,122,0.14)" },
      "&:hover fieldset": { borderColor: isDark ? "rgba(255,255,255,0.22)" : "rgba(0,53,122,0.3)" },
      "&.Mui-focused fieldset": { borderColor: BRAND.navy, borderWidth: "2px" },
      "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(0,53,122,0.08)" },
    },
    "& .MuiOutlinedInput-input": { py: "13px", px: "14px" },
  };

  // 2026-10-26 fix (ui-ux-checker finding, real UI sweep) — submitting
  // completely empty email/password used to go all the way to the
  // server and come back with a generic "Login failed." — no "Email is
  // required" feedback at all. Disabling submit until both fields are
  // non-empty matches the pattern already used correctly elsewhere in
  // this app's own invite/create dialogs.
  const canSubmit = email.trim() !== "" && password !== "";

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    setMessage(null);
    try {
      const res = await axios.post(`${API_BASE}/email-login`, {
        email: email.trim().toLowerCase(),
        password,
      });
      setUser({
        email:                 res.data.email,
        name:                  res.data.name,
        token:                 res.data.token,
        role:                  res.data.role || "patient",
        practice_name:         res.data.practice_name || "",
        npi:                   res.data.npi || "",
        force_password_change: res.data.force_password_change || false,
        payment_confirmed:     res.data.payment_confirmed || false,
        // 2026-10-23 addition (clinician invite-only build, Piece 4) —
        // only ever present/true for a clinician; undefined for patients,
        // which ProtectedRoute's new check treats as falsy.
        identity_profile_incomplete: res.data.identity_profile_incomplete || false,
        // 2026-10-23 addition (ui-ux-checker finding, lab-brand routing)
        // — only ever present for a sales_rep; undefined otherwise.
        lab_brand:         res.data.lab_brand || "",
        lab_brand_display: res.data.lab_brand_display || "",
      });
      if (res.data.force_password_change) {
        navigate("/change-password?forced=true", { replace: true });
      } else {
        navigate("/home", { replace: true });
      }
    } catch (err) {
      setLoading(false);
      const status = err.response?.status;
      const detail = extractErrorDetail(err, "Login failed. Please try again.");
      // 402 = payment pending — show as warning, not error
      setMessage({ severity: status === 402 ? "warning" : "error", text: detail });
    }
  };

  return (
    <Box sx={{
      minHeight: "100vh", bgcolor: bg,
      display: "flex", flexDirection: "column",
      overflow: "hidden", position: "relative",
    }}>

      {/* Animated background blobs */}
      <Box sx={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }}>
        {[
          { top: "-10%", left: "-5%", size: 500, color: isDark ? "rgba(0,53,122,0.18)" : "rgba(0,53,122,0.07)", delay: 0 },
          { top: "60%",  right: "-8%", size: 400, color: isDark ? "rgba(192,168,140,0.08)" : "rgba(192,168,140,0.12)", delay: 1.5 },
          { top: "30%",  left: "50%",  size: 300, color: isDark ? "rgba(0,53,122,0.10)" : "rgba(0,53,122,0.05)", delay: 0.8 },
        ].map((b, i) => (
          <motion.div key={i} style={{
            position: "absolute", top: b.top, left: b.left, right: b.right,
            width: b.size, height: b.size, borderRadius: "50%",
            background: b.color, filter: "blur(80px)",
          }}
            animate={{ scale: [1, 1.12, 1], opacity: [0.8, 1, 0.8] }}
            transition={{ duration: 8 + i * 2, repeat: 2, delay: b.delay, ease: "easeInOut" }}
          />
        ))}
      </Box>

      {/* Header */}
      <motion.div initial={{ y: -64, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }} style={{ position: "relative", zIndex: 10 }}>
        <Box sx={{ bgcolor: isDark ? "rgba(2,6,15,0.88)" : "rgba(0,31,77,0.97)",
          backdropFilter: "blur(16px)", borderBottom: "1px solid rgba(201,169,110,0.2)" }}>
          <Box sx={{ maxWidth: 1200, mx: "auto", px: 3, py: 1, minHeight: 68,
            display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", rowGap: 1 }}>
            <Box component="a" href="https://labarium.com/" target="_blank" rel="noopener noreferrer"
              sx={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
              <img src="/logo-labarium-dark.png" alt="Labarium Diagnostic Systems" style={{ height: 46, width: "auto" }} />
            </Box>
            <ThemeToggle />
          </Box>
        </Box>
      </motion.div>

      {/* Main content */}
      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
        px: 3, py: 6, position: "relative", zIndex: 10, gap: { md: 8 },
        flexDirection: { xs: "column", md: "row" } }}>

        {/* Left — branding */}
        <motion.div initial={{ x: -40, opacity: 0 }} animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.15, ease: "easeOut" }}>
          <Box sx={{ maxWidth: 380, textAlign: { xs: "center", md: "left" } }}>
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1,
              bgcolor: isDark ? "rgba(192,168,140,0.12)" : "rgba(0,53,122,0.08)",
              border: `1px solid ${isDark ? "rgba(192,168,140,0.2)" : "rgba(0,53,122,0.12)"}`,
              borderRadius: 99, px: 2, py: 0.5, mb: 3 }}>
              <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: BRAND.gold,
                "@keyframes pulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.3 } },
                animation: "pulse 2s infinite" }} />
              <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: BRAND.gold, letterSpacing: "0.1em" }}>
                LABARIUM AI — LIVE
              </Typography>
            </Box>

            <Typography sx={{ fontSize: { xs: 32, md: 42 }, fontWeight: 800,
              color: isDark ? "#f1f5f9" : BRAND.navy, lineHeight: 1.2, mb: 2 }}>
              Labarium<br />
              <Box component="span" sx={{
                background: `linear-gradient(135deg, ${BRAND.gold}, #dfc08a)`,
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
              }}>Intelligence</Box>
            </Typography>

            <Typography sx={{ fontSize: 17, color: isDark ? "#94a3b8" : "#4a5568", lineHeight: 1.75, mb: 4 }}>
              AI-powered diagnostic decision support for patients and clinicians — from Labarium Diagnostic Systems.
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5,
              alignItems: { xs: "center", md: "flex-start" } }}>
              {FEATURES.map((f, i) => (
                <motion.div key={i} initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.3 + i * 0.08, duration: 0.4 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                    <Box sx={{ fontSize: 20 }}>{f.icon}</Box>
                    <Typography sx={{ fontSize: 16, color: isDark ? "#e2e8f0" : "#1e293b", fontWeight: 500 }}>
                      {f.text}
                    </Typography>
                  </Box>
                </motion.div>
              ))}
            </Box>
          </Box>
        </motion.div>

        {/* Right — login card */}
        <motion.div initial={{ y: 30, opacity: 0, scale: 0.97 }} animate={{ y: 0, opacity: 1, scale: 1 }}
          transition={{ duration: 0.55, delay: 0.2, ease: [0.34, 1.56, 0.64, 1] }}>
          <Box sx={{
            width: { xs: "100%", sm: 480 }, maxWidth: 480, borderRadius: 3, overflow: "hidden",
            border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,53,122,0.1)"}`,
            boxShadow: isDark
              ? "0 24px 80px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05)"
              : "0 24px 80px rgba(0,53,122,0.12), 0 4px 16px rgba(0,53,122,0.06)",
            bgcolor: cardBg, backdropFilter: "blur(20px)",
          }}>
            {/* Top stripe */}
            <Box sx={{ height: 4, background: `linear-gradient(to right, ${BRAND.navy}, ${BRAND.gold})` }} />

            {/* Card header */}
            <Box sx={{ bgcolor: BRAND.navy, px: 4, pt: 3, pb: 3.5, position: "relative", overflow: "hidden" }}>
              <Box sx={{ position: "absolute", top: -40, right: -40, width: 140, height: 140,
                borderRadius: "50%", border: "1px solid rgba(192,168,140,0.15)" }} />
              <Box sx={{ position: "absolute", top: -20, right: -20, width: 90, height: 90,
                borderRadius: "50%", border: "1px solid rgba(192,168,140,0.2)" }} />
              <Typography sx={{ fontSize: 13.5, fontWeight: 700, letterSpacing: "0.18em", color: BRAND.gold, mb: 1 }}>
                SECURE SIGN IN
              </Typography>
              <Typography sx={{ fontSize: 26, fontWeight: 700, color: "#fff", lineHeight: 1.3 }}>
                Labarium Intelligence
              </Typography>
              <Typography sx={{ fontSize: 15, color: "rgba(255,255,255,0.55)", mt: 0.5 }}>
                Diagnostic Decision Platform · Labarium Diagnostic Systems
              </Typography>
            </Box>

            {/* Card body */}
            <Box sx={{ px: 4, py: 4 }}>
              <AnimatePresence mode="wait">
                {message && (
                  <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25 }}>
                    <Alert severity={message.severity} onClose={() => setMessage(null)}
                      sx={{ mb: 2.5, borderRadius: 2, fontSize: 14.5 }}>
                      {message.text}
                    </Alert>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Payment-success banner — shown when Stripe redirects back */}
              {paymentJustCompleted && (
                <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}>
                  <Box sx={{
                    display: "flex", alignItems: "flex-start", gap: 1.5,
                    bgcolor: isDark ? "rgba(34,197,94,0.1)" : "rgba(34,197,94,0.08)",
                    border: "1px solid rgba(34,197,94,0.3)",
                    borderRadius: "10px", px: 2, py: 1.5, mb: 2.5,
                  }}>
                    <Box sx={{ fontSize: 18, flexShrink: 0, mt: 0.1 }}>✓</Box>
                    <Box>
                      <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#16a34a", mb: 0.25 }}>
                        Payment confirmed!
                      </Typography>
                      <Typography sx={{ fontSize: 14, color: isDark ? "#94a3b8" : "#475569", lineHeight: 1.6 }}>
                        Your account is being activated. Check your email and phone — your login
                        credentials will arrive within a few minutes.
                      </Typography>
                    </Box>
                  </Box>
                </motion.div>
              )}

              <Box component="form" onSubmit={handleEmailLogin} noValidate>
                <Field label="Email address" required isDark={isDark}>
                  <TextField hiddenLabel type="email" fullWidth required
                    placeholder="you@labarium.com"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    sx={inputSx} />
                </Field>
                <Field label="Password" required isDark={isDark}
                  hint={
                    <Box component={Link} to="/forgot-password"
                      sx={{ fontSize: 14, color: BRAND.gold, textDecoration: "underline", fontWeight: 600 }}>
                      Forgot password?
                    </Box>
                  }>
                  <TextField hiddenLabel fullWidth required
                    type={showPw ? "text" : "password"}
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    sx={inputSx}
                    slotProps={{ input: { endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPw((p) => !p)} edge="end">
                          {showPw ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )}}} />
                </Field>
                <Button type="submit" fullWidth variant="contained" disabled={loading || !canSubmit}
                  sx={{
                    mt: 0.5, mb: 2.5,
                    background: `linear-gradient(135deg, ${BRAND.navy} 0%, #004299 100%)`,
                    borderRadius: "10px", py: 1.6, fontWeight: 700, textTransform: "none", fontSize: 17,
                    boxShadow: "0 4px 14px rgba(0,53,122,0.35)",
                    "&:hover": { boxShadow: "0 6px 20px rgba(0,53,122,0.45)", bgcolor: BRAND.navyDark },
                  }}>
                  {loading ? <CircularProgress size={24} sx={{ color: "#fff" }} /> : "Sign In"}
                </Button>
              </Box>

              <Typography sx={{ textAlign: "center", fontSize: 15, color: textSub, mb: 2.5 }}>
                Don't have an account?{" "}
                <Box component={Link} to="/register"
                  sx={{ color: BRAND.gold, textDecoration: "underline", fontWeight: 600 }}>
                  Create one
                </Box>
              </Typography>

              <Box sx={{ pt: 2, borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#f1f5f9"}` }}>
                <Typography sx={{ fontSize: 14, color: isDark ? "#64748b" : "#64748b",
                  textAlign: "center", lineHeight: 1.7 }}>
                  By signing in, you agree to our{" "}
                  <Box component={Link} to="/terms"
                    sx={{ color: BRAND.gold, textDecoration: "underline", fontWeight: 600, "&:hover": { opacity: 0.75 } }}>
                    Terms of Use
                  </Box>{" "}and{" "}
                  <Box component={Link} to="/privacy"
                    sx={{ color: BRAND.gold, textDecoration: "underline", fontWeight: 600, "&:hover": { opacity: 0.75 } }}>
                    Privacy Policy
                  </Box>.
                </Typography>
              </Box>
            </Box>
          </Box>

          <Typography sx={{ mt: 2.5, fontSize: 14.5, color: isDark ? "#334155" : "#9ca3af", textAlign: "center" }}>
            AI analysis does not constitute medical advice. Always consult a licensed provider.
          </Typography>
        </motion.div>
      </Box>

      {/* Footer */}
      <Box sx={{ bgcolor: isDark ? "rgba(2,6,15,0.9)" : BRAND.navyDark,
        backdropFilter: "blur(10px)", py: 2, textAlign: "center",
        position: "relative", zIndex: 10, borderTop: "1px solid rgba(255,255,255,0.05)" }}>
        <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.25)", letterSpacing: "0.05em" }}>
          © {new Date().getFullYear()} LABARIUM DIAGNOSTIC SYSTEMS, INC. ALL RIGHTS RESERVED.
          {" "}
          <Box component={Link} to="/admin-login"
            sx={{ color: "rgba(255,255,255,0.25)", textDecoration: "none", "&:hover": { color: "rgba(255,255,255,0.45)" } }}>
            · Admin
          </Box>
        </Typography>
      </Box>
    </Box>
  );
}

export default LoginPage;
